import { motion } from "framer-motion";
import { useEffect, useState } from "react";

const GHOSTS = [
    { name: "Derek Klein", title: "VP Sales · Acme Corp", status: "bounced" },
    { name: "Priya Raman", title: "Director · Initech", status: "left-co" },
    { name: "Marcus Webb", title: "Head RevOps · Globex", status: "bounced" },
    { name: "Lena Ostrowski", title: "CRO · Massive Dyn.", status: "left-co" },
    { name: "Tomás Ferreira", title: "VP Marketing · Hooli", status: "stale" },
];

const CLEAN = [
    { name: "Derek Klein", title: "CRO · Linear Motion", signal: "new-role" },
    {
        name: "Priya Raman",
        title: "SVP RevOps · Ramp Labs",
        signal: "verified",
    },
    { name: "Marcus Webb", title: "VP Sales · Lumen AI", signal: "new-role" },
    {
        name: "Lena Ostrowski",
        title: "Chief Growth · Northbeam",
        signal: "verified",
    },
    {
        name: "Tomás Ferreira",
        title: "VP Marketing · Orbit.so",
        signal: "verified",
    },
];

function DeadCard({ person, index }) {
    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.08, duration: 0.4 }}
            className="relative flex items-center gap-3 rounded-xl border border-white/5 bg-[#0d0d0d] px-3.5 py-3"
        >
            <div className="h-9 w-9 rounded-lg bg-neutral-800 flex items-center justify-center text-[11px] font-mono text-neutral-600">
                {person.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-neutral-500 line-through decoration-neutral-700">
                    {person.name}
                </div>
                <div className="text-[11px] text-neutral-600 truncate">
                    {person.title}
                </div>
            </div>
            <StatusPill status={person.status} />
        </motion.div>
    );
}

function StatusPill({ status }) {
    const map = {
        bounced: {
            text: "BOUNCED",
            color: "text-red-400/80 bg-red-500/10 border-red-500/15",
        },
        "left-co": {
            text: "LEFT CO.",
            color: "text-amber-400/80 bg-amber-500/10 border-amber-500/15",
        },
        stale: {
            text: "STALE 18mo",
            color: "text-neutral-400 bg-neutral-500/10 border-neutral-500/15",
        },
    };
    const s = map[status] || map.stale;
    return (
        <span
            className={`text-[9px] font-mono font-semibold tracking-wider rounded-md border px-1.5 py-0.5 ${s.color}`}
        >
            {s.text}
        </span>
    );
}

function CleanCard({ person, index }) {
    return (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + index * 0.08, duration: 0.5 }}
            className="group relative flex items-center gap-3 rounded-xl border border-[#10B981]/25 bg-gradient-to-br from-[#10B981]/[0.07] to-transparent px-3.5 py-3 hover:border-[#10B981]/50 transition-colors"
            style={{ boxShadow: "inset 0 0 0 1px rgba(16,185,129,0.04)" }}
        >
            <div className="h-9 w-9 rounded-lg bg-[#10B981]/10 border border-[#10B981]/30 flex items-center justify-center text-[11px] font-mono font-semibold text-[#34D399]">
                {person.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-medium text-white">
                        {person.name}
                    </span>
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        className="text-[#10B981]"
                    >
                        <path
                            fill="currentColor"
                            d="M6 0a6 6 0 100 12A6 6 0 006 0zm-.8 8.7L2.7 6.2l.9-.9 1.6 1.6 3.2-3.2.9.9L5.2 8.7z"
                        />
                    </svg>
                </div>
                <div className="text-[11px] text-neutral-400 truncate">
                    {person.title}
                </div>
            </div>
            <span className="text-[9px] font-mono font-semibold tracking-wider rounded-md border border-[#10B981]/25 bg-[#10B981]/10 px-1.5 py-0.5 text-[#34D399]">
                {person.signal === "new-role" ? "NEW ROLE" : "VERIFIED"}
            </span>
        </motion.div>
    );
}

export default function PipelineVisual() {
    const [side, setSide] = useState("dirty"); // dirty | clean
    const [pct, setPct] = useState(31);

    useEffect(() => {
        const i = setInterval(() => {
            setSide((s) => (s === "dirty" ? "clean" : "dirty"));
        }, 4200);
        return () => clearInterval(i);
    }, []);

    useEffect(() => {
        let target = side === "dirty" ? 31 : 100;
        const i = setInterval(() => {
            setPct((p) => {
                if (p === target) return p;
                const step = target > p ? 1 : -1;
                return p + step;
            });
        }, 18);
        return () => clearInterval(i);
    }, [side]);

    return (
        <div
            data-testid="pipeline-visual"
            className="relative rounded-2xl border border-white/[0.08] bg-[#070707] p-4 sm:p-5 overflow-hidden"
            style={{
                boxShadow:
                    "0 40px 80px -30px rgba(16,185,129,0.25), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
        >
            {/* Header strip */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-red-400/70" />
                    <span className="h-2 w-2 rounded-full bg-amber-400/60" />
                    <span className="h-2 w-2 rounded-full bg-[#10B981]" />
                    <span className="ml-2 text-[10px] tracking-[0.2em] uppercase font-mono text-neutral-500">
                        Salesforce · Decision Makers
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setSide("dirty")}
                        className={`text-[10px] font-mono px-2 py-1 rounded border transition-colors ${side === "dirty" ? "border-red-500/40 text-red-300 bg-red-500/10" : "border-white/5 text-neutral-500 hover:text-neutral-300"}`}
                    >
                        BEFORE
                    </button>
                    <button
                        onClick={() => setSide("clean")}
                        className={`text-[10px] font-mono px-2 py-1 rounded border transition-colors ${side === "clean" ? "border-[#10B981]/40 text-[#34D399] bg-[#10B981]/10" : "border-white/5 text-neutral-500 hover:text-neutral-300"}`}
                    >
                        AFTER
                    </button>
                </div>
            </div>

            {/* Pipeline health bar */}
            <div className="mb-4">
                <div className="flex items-center justify-between text-[10px] font-mono text-neutral-500 mb-1.5">
                    <span>PIPELINE HEALTH</span>
                    <motion.span
                        key={pct}
                        initial={{ opacity: 0.5 }}
                        animate={{ opacity: 1 }}
                        className={
                            side === "dirty"
                                ? "text-red-300"
                                : "text-[#34D399]"
                        }
                    >
                        {pct}%
                    </motion.span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-neutral-900 overflow-hidden">
                    <motion.div
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.4 }}
                        className={`h-full ${side === "dirty" ? "bg-gradient-to-r from-red-500/70 to-amber-400/70" : "bg-gradient-to-r from-[#10B981] to-[#34D399]"}`}
                    />
                </div>
            </div>

            {/* Cards */}
            <div className="space-y-2 min-h-[320px]">
                {side === "dirty"
                    ? GHOSTS.map((p, i) => (
                          <DeadCard key={`d-${i}`} person={p} index={i} />
                      ))
                    : CLEAN.map((p, i) => (
                          <CleanCard key={`c-${i}`} person={p} index={i} />
                      ))}
            </div>

            {/* Bottom KPI row */}
            <div className="mt-4 grid grid-cols-3 gap-2 pt-3 border-t border-white/5">
                <Kpi
                    label="Ghost leads"
                    value={side === "dirty" ? "1,284" : "0"}
                    bad={side === "dirty"}
                />
                <Kpi
                    label="Reachable"
                    value={side === "dirty" ? "61%" : "100%"}
                    good={side === "clean"}
                />
                <Kpi
                    label="Bounce rate"
                    value={side === "dirty" ? "14.7%" : "0.8%"}
                    good={side === "clean"}
                />
            </div>

            {/* Ambient volt glow */}
            <div
                className="pointer-events-none absolute -bottom-20 -right-16 h-64 w-64 rounded-full blur-3xl transition-opacity duration-1000"
                style={{
                    background:
                        "radial-gradient(circle, rgba(16,185,129,0.22), transparent 70%)",
                    opacity: side === "clean" ? 1 : 0.15,
                }}
            />
        </div>
    );
}

function Kpi({ label, value, good, bad }) {
    return (
        <div className="rounded-lg border border-white/5 bg-black/40 px-3 py-2">
            <div className="text-[9px] font-mono tracking-widest text-neutral-600 uppercase">
                {label}
            </div>
            <div
                className={`font-display text-lg font-semibold mt-0.5 ${good ? "text-[#34D399]" : bad ? "text-red-300" : "text-white"}`}
            >
                {value}
            </div>
        </div>
    );
}
